import { Link, type LinkProps } from "react-router-dom";
import { INDEXABLE_BROWSE_PATH_SET } from "@shared/generated-indexable-pages";

/**
 * A drop-in replacement for react-router's <Link> that adds rel="nofollow"
 * when the destination is a non-indexable /browse combo page.
 *
 * Why: thin /browse combo pages (state/city/treatment with too few listings)
 * are intentionally noindex'd and excluded from the sitemap. But if the app
 * keeps emitting crawlable <a href> links to them, Google still discovers and
 * queues them ("Discovered – currently not indexed"), wasting crawl budget that
 * should go to our real pages. nofollow tells Google not to queue them while
 * keeping the link fully usable for humans.
 *
 * Indexable destinations (and any non-/browse link) pass through unchanged.
 */
export function SeoLink({ to, rel, ...props }: LinkProps) {
  const path =
    typeof to === "string" ? to.split(/[?#]/)[0].replace(/\/+$/, "") || "/" : "";
  const isThinCombo =
    path.startsWith("/browse/") && !INDEXABLE_BROWSE_PATH_SET.has(path);
  const mergedRel = isThinCombo
    ? [rel, "nofollow"].filter(Boolean).join(" ")
    : rel;
  return <Link to={to} rel={mergedRel} {...props} />;
}
